import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import NotificationsBell from "./NotificationsBell";
import { LogOut, User, Plus, MessageSquare, Heart, CalendarDays, Star, Shield, LayoutDashboard } from "lucide-react";

const linkCls = ({ isActive }) =>
  `text-sm font-medium transition-colors duration-200 ${
    isActive ? "text-[#143122]" : "text-[#1A1F1C]/70 hover:text-[#143122]"
  }`;

export default function Navbar() {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header
      data-testid="main-navbar"
      className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-[#E6E2D8]"
      style={{ zIndex: 10000 }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-6">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2">
          <span className="font-serif-display text-2xl font-semibold tracking-tight text-[#143122]">
            Kavioura
          </span>
          <span className="hidden md:inline-block h-1.5 w-1.5 rounded-full bg-[#D1C6B4]" />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink data-testid="nav-horses" to="/horses" className={linkCls}>{t("horses")}</NavLink>
          <NavLink data-testid="nav-rentals" to="/rentals" className={linkCls}>{t("nav_rentals")}</NavLink>
          <NavLink data-testid="nav-tack" to="/marketplace?type=tack" className={linkCls}>{t("tack")}</NavLink>
          <NavLink data-testid="nav-boarding" to="/marketplace?type=boarding" className={linkCls}>{t("boarding")}</NavLink>
          <NavLink data-testid="nav-jobs" to="/marketplace?type=job" className={linkCls}>{t("nav_jobs")}</NavLink>
          <NavLink data-testid="nav-services" to="/marketplace?type=service" className={linkCls}>{t("nav_services")}</NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger data-testid="lang-switcher" className="w-[80px] h-9 bg-[#F4F1E9] border-[#E6E2D8] text-xs uppercase tracking-widest">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-[#E6E2D8]">
              <SelectItem data-testid="lang-en" value="en">EN</SelectItem>
              <SelectItem data-testid="lang-fi" value="fi">FI</SelectItem>
              <SelectItem data-testid="lang-sv" value="sv">SV</SelectItem>
            </SelectContent>
          </Select>

          {user ? (
            <>
              <button
                data-testid="nav-new-listing"
                onClick={() => navigate("/new")}
                className="kav-btn-primary text-sm hidden sm:inline-flex"
              >
                <Plus size={16} strokeWidth={1.75} /> {t("create_listing")}
              </button>
              <NavLink data-testid="nav-favorites" to="/favorites" className={linkCls} title={t("favorites")}>
                <Heart size={20} strokeWidth={1.5} />
              </NavLink>
              <NotificationsBell />
              <NavLink data-testid="nav-bookings" to="/bookings/mine" className={linkCls} title={t("bookings")}>
                <CalendarDays size={20} strokeWidth={1.5} />
              </NavLink>
              <NavLink data-testid="nav-reviews" to="/reviews/inbox" className={linkCls} title={t("reviews")}>
                <Star size={20} strokeWidth={1.5} />
              </NavLink>
              {user.is_admin && (
                <NavLink data-testid="nav-admin" to="/admin/feedback" className={linkCls} title={t("admin")}>
                  <Shield size={20} strokeWidth={1.5} />
                </NavLink>
              )}
              <NavLink data-testid="nav-messages" to="/messages" className={linkCls} title={t("nav_messages")}>
                <MessageSquare size={20} strokeWidth={1.5} />
              </NavLink>
              <NavLink data-testid="nav-profile" to={`/users/${user.id}`} className={linkCls} title={t("my_profile")}>
                <User size={20} strokeWidth={1.5} />
              </NavLink>
              <NavLink data-testid="nav-dashboard" to="/dashboard" className={linkCls} title={t("nav_dashboard")}>
                <LayoutDashboard size={20} strokeWidth={1.5} />
              </NavLink>
              <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }} className="text-[#1A1F1C]/70 hover:text-[#143122] transition-colors">
                <LogOut size={18} strokeWidth={1.5} />
              </button>
            </>
          ) : (
            <>
              <Link data-testid="nav-login" to="/login" className="text-sm text-[#143122] hover:underline">{t("login")}</Link>
              <Link data-testid="nav-signup" to="/register" className="kav-btn-primary text-sm">{t("signup")}</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
